/**
 * Hook for validating encoder configuration before streaming
 * Uses WebCodecs API to check if the current encoder settings are compatible
 */

import { useCallback } from "react";

import { useTranslation } from "react-i18next";

import type { TFunction } from "i18next";

import { sileo } from "sileo";

import { openDatabase } from "../Utils/db";

import {
  DEFAULT_ENCODERS_CONFIG,
  type EncodersConfig,
} from "../entities/webcodecs/config";

import {
  validateVideoConfig,
  validateAudioConfig,
  type VideoValidationConfig,
  type AudioValidationConfig,
} from "../screens/Settings/SubComponents/Encoders/encoders.errors.handler";

export interface EncoderValidationResult {
  valid: boolean;

  videoValid: boolean;

  audioValid: boolean;

  videoMessage?: string;

  audioMessage?: string;
}

export function useEncoderValidation() {
  const { t } = useTranslation("settings");

  /**
   * Load current encoder configuration from database
   */

  const loadEncodersConfig = useCallback(async (): Promise<EncodersConfig> => {
    try {
      const db = await openDatabase();

      const configUsers = await db?.config.toArray();

      const userId = localStorage.getItem("@Eriol/system/userId");

      const userConfigDB = configUsers?.find((item) => item.userId === userId);

      const savedEncoders = userConfigDB?.configuration?.encoders;

      return {
        ...DEFAULT_ENCODERS_CONFIG,

        ...(savedEncoders as Partial<EncodersConfig>),
      };
    } catch (error) {
      console.warn("Could not load encoders config, using defaults:", error);

      return DEFAULT_ENCODERS_CONFIG;
    }
  }, []);

  /**
   * Validate both video and audio encoder configurations
   * Returns validation result with detailed error messages
   */

  const validateEncoders =
    useCallback(async (): Promise<EncoderValidationResult> => {
      const encodersConfig = await loadEncodersConfig();

      const videoConfig: VideoValidationConfig = {
        codecProfile: encodersConfig.codecProfile,

        width: 1920, // Default to 1080p for validation

        height: 1080,

        bitrate: encodersConfig.bitrate,

        framerate: encodersConfig.framerate,

        hardwareAcceleration: encodersConfig.hardwareAcceleration,
      };

      const audioConfig: AudioValidationConfig = {
        audioCodec: encodersConfig.audioCodec,

        audioProfile: encodersConfig.audioProfile,

        audioSampleRate: encodersConfig.audioSampleRate,

        audioChannels: encodersConfig.audioChannels,

        audioBitrate: encodersConfig.audioBitrate,
      };

      // Run validations

      const tTranslation = t as unknown as TFunction<"translation">;

      const [videoResult, audioResult] = await Promise.all([
        validateVideoConfig(videoConfig, tTranslation),

        validateAudioConfig(audioConfig, tTranslation),
      ]);

      return {
        valid: videoResult.success && audioResult.success,

        videoValid: videoResult.success,

        audioValid: audioResult.success,

        videoMessage: videoResult.success ? undefined : videoResult.message,

        audioMessage: audioResult.success ? undefined : audioResult.message,
      };
    }, [loadEncodersConfig, t]);

  const validateBeforeBroadcast = useCallback(async (): Promise<boolean> => {
    const result = await validateEncoders();

    if (!result.valid) {
      if (!result.videoValid && result.videoMessage) {
        sileo.error({
          title: result.videoMessage,

          duration: 10000,

          position: "top-center",
        });
      }

      if (!result.audioValid && result.audioMessage) {
        sileo.error({
          title: result.audioMessage,

          duration: 10000,

          position: "top-center",
        });
      }

      sileo.info({
        title: t("SETTINGS.FORMS.ENCODERS.ERRORS.GUIDANCE.profile"),

        duration: 8000,

        position: "top-center",
      });

      return false;
    }

    return true;
  }, [validateEncoders, t]);

  return {
    validateEncoders,

    validateBeforeBroadcast,

    loadEncodersConfig,
  };
}
